"use client";

import { useCallback, useMemo } from "react";

import { removeOrFilterValues } from "@/lib/facet-filter-state";
import { normalizeStringArray } from "@garimpa/lib/normalizeStringArray";
import {
  isReleaseSearchFilterEmpty,
  type FacetedSearchState,
  type RangeMatcher,
  type ReleaseSearchFilter,
  type StringMatcher,
} from "@garimpa/lib/searchState";

type MatchMode = "any" | "all";

type SetValuesOptions = {
  match?: MatchMode;
  clearValues?: readonly string[];
  clearOrValues?: readonly string[];
};

const DEFAULT_CLEAR_VALUES = ["", "all", "auto"] as const;

function normalizeValues(
  value: string | readonly string[] | null | undefined,
  clearValues: readonly string[] = DEFAULT_CLEAR_VALUES,
): string[] {
  const values = normalizeStringArray(value);
  return values.filter((item) => !clearValues.includes(item));
}

function matcherFromValues(values: string[], match: MatchMode): StringMatcher | undefined {
  if (!values.length) return undefined;
  if (match === "all" && values.length > 1) return { all: values };
  return values.length === 1 ? values[0]! : values;
}

function setMatcher(
  filter: ReleaseSearchFilter,
  key: string,
  value: string | readonly string[] | null | undefined,
  options: SetValuesOptions = {},
) {
  const values = normalizeValues(value, options.clearValues);
  const matcher = matcherFromValues(values, options.match ?? "any");

  if (key === "artist.country") {
    filter.artist = matcher ? { ...filter.artist, country: matcher } : undefined;
    return;
  }
  if (key === "year" || key === "OR") return;
  filter[key as keyof Omit<ReleaseSearchFilter, "artist" | "year" | "OR">] = matcher;
}

function setYearMatcher(filter: ReleaseSearchFilter, range: RangeMatcher | null | undefined) {
  if (!range || (range.length === 2 && range[0] == null && range[1] == null)) {
    filter.year = undefined;
    return;
  }
  filter.year = range;
}

function compactState(state: FacetedSearchState): FacetedSearchState | undefined {
  const next: FacetedSearchState = {};
  if (state.q) next.q = state.q;
  if (state.sort) next.sort = state.sort;
  if (state.page && state.page > 1) next.page = state.page;
  if (!isReleaseSearchFilterEmpty(state.filter)) next.filter = state.filter;
  return Object.keys(next).length ? next : undefined;
}

export function useSearchFilterBuilder(
  state: FacetedSearchState | undefined,
  onChange: (next: FacetedSearchState | undefined) => void,
) {
  const filter = useMemo<ReleaseSearchFilter>(() => ({ ...state?.filter }), [state?.filter]);

  const updateFilter = useCallback(
    (updater: (draft: ReleaseSearchFilter) => ReleaseSearchFilter | void) => {
      const draft: ReleaseSearchFilter = { ...state?.filter };
      const nextFilter = updater(draft) ?? draft;
      onChange(compactState({ ...state, filter: nextFilter, page: undefined }));
    },
    [onChange, state],
  );

  const setValues = useCallback(
    (
      key: string,
      value: string | readonly string[] | null | undefined,
      options?: SetValuesOptions,
    ) =>
      updateFilter((draft) => {
        const nextDraft = options?.clearOrValues?.length
          ? removeOrFilterValues(draft, key, options.clearOrValues)
          : draft;
        setMatcher(nextDraft, key, value, options);
        return nextDraft;
      }),
    [updateFilter],
  );

  const setYear = useCallback(
    (range: RangeMatcher | null | undefined) =>
      updateFilter((draft) => setYearMatcher(draft, range)),
    [updateFilter],
  );

  const clear = useCallback(
    (key: string) =>
      updateFilter((draft) => {
        if (key === "year") draft.year = undefined;
        else setMatcher(draft, key, undefined);
      }),
    [updateFilter],
  );

  const setCompanyOrLabel = useCallback(
    (value: string | null | undefined) =>
      updateFilter((draft) => {
        const values = normalizeValues(value);
        draft.OR = values[0] ? [{ label: values[0] }, { company: values[0] }] : undefined;
      }),
    [updateFilter],
  );

  const setCompany = useCallback(
    (value: string | readonly string[] | null | undefined, options?: SetValuesOptions) =>
      setValues("company", value, { match: "all", ...options }),
    [setValues],
  );

  const setLabel = useCallback(
    (value: string | readonly string[] | null | undefined, options?: SetValuesOptions) =>
      setValues("label", value, { match: "all", ...options }),
    [setValues],
  );

  const clearAll = useCallback(() => {
    onChange(compactState({ q: state?.q }));
  }, [onChange, state?.q]);

  return {
    filter,
    setValues,
    setYear,
    setCompany,
    setLabel,
    setCompanyOrLabel,
    clear,
    clearAll,
  };
}
